import React, { useState, useEffect } from 'react';
import { useAlert } from '../../../providers/AlertProvider';
import { useAuthProvider } from '../../../providers/AuthProvider';
import { validatePassword } from "../../../api/profiles";
import { downloadReportCSV } from '../../lib/functions';
import Button from '../Button';
import logger from "../../../utils/logger";

import RequestPassword from "../RequestPassword";
import "../../css/GlobalStyles.css";

const InboundReport = ({ inboundData = [], theme }) => {
    const { profile } = useAuthProvider();
    const { showAlert } = useAlert();

    const [tableData, setTableData] = useState([]);
    const [isFiltered, setIsFiltered] = useState(false);
    const [summaryStats, setSummaryStats] = useState({});

    const isLightTheme = theme === 'light';

    const displayColumns = [
        { key: 'date', label: 'Date' },
        { key: 'customer_company', label: 'Customer' },
        { key: 'product_names', label: 'Products' },
        { key: 'total_pallet_count', label: 'Pallet Count' },
        { key: 'total_quantity', label: 'Total Quantity' },
        { key: 'entry_count', label: 'Entries' },
        { key: 'id_range', label: 'ID Range' },
    ];

    // Request Password States =======================================================================================================
    const [openRequestPassword, setOpenRequestPassword] = useState(false);
    const [allowDownload, setAllowDownload] = useState(false);

    useEffect(() => {
        setOpenRequestPassword(true); // Open password modal on component mount
        //showData(); // for debugging
    }, []);
    
    const handlePasswordSubmission = async (password) => {
        const email = profile.email;
    
        try {
            const { status } = await validatePassword(password, email);
            if (status === 200) {
                showData();
                setAllowDownload(true); // Allow download if password is valid
            } else {
                logger.log("Invalid password.");
                inboundData.length = 0; // Clear inboundData if password is invalid
                showAlert({
                    message: 'Password verification failed.',
                    type: 'danger',
                    buttons: [{ type: 'danger', label: 'Close' }],
                });
            }
        } catch (error) {
            logger.error("Error verifying password:", error);
            showAlert({
                message: 'An error occurred during password verification.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        }
        setOpenRequestPassword(false); // Close the modal
    };
    
    const showData = () => {
        console.log("InboundReport - showData called with:", inboundData);
        console.log("InboundReport - inboundData length:", inboundData?.length);
        
        if (inboundData && inboundData.length > 0) {
            // Format the data for display
            const formatted = inboundData.map((entry) => {
                const newEntry = {};
                displayColumns.forEach(({ key }) => {
                    if (key === 'date') {
                        const dateObj = new Date(entry[key]);
                        newEntry[key] = isNaN(dateObj)
                            ? entry[key]
                            : `${String(dateObj.getDate()).padStart(2, '0')}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${dateObj.getFullYear()}`;
                    } else {
                        newEntry[key] = entry[key] ?? '—';
                    }
                });
                return newEntry;
            });
            setTableData(formatted);
            calculateSummaryStats(formatted);
        }
    };

    const calculateSummaryStats = (data) => {
        if (!data || data.length === 0) {
            setSummaryStats({});
            return;
        }

        const stats = {
            totalOrders: data.length,
            totalPallets: data.reduce((sum, item) => sum + (parseInt(item.total_pallet_count) || 0), 0),
            totalQuantity: data.reduce((sum, item) => sum + (parseFloat(item.total_quantity) || 0), 0),
            uniqueCustomers: [...new Set(data.map(item => item.customer_company))].length,
            totalEntries: data.reduce((sum, item) => sum + (parseInt(item.entry_count) || 0), 0),
        };

        setSummaryStats(stats);
    };

    // Calculate filtered totals for table footer
    const calculateFilteredTotals = () => {
        if (!tableData || tableData.length === 0) {
            return {
                totalPallets: 0,
                totalQuantity: 0,
                totalEntries: 0
            };
        }

        return {
            totalPallets: tableData.reduce((sum, item) => sum + (parseInt(item.total_pallet_count) || 0), 0),
            totalQuantity: tableData.reduce((sum, item) => sum + (parseFloat(item.total_quantity) || 0), 0),
            totalEntries: tableData.reduce((sum, item) => sum + (parseInt(item.entry_count) || 0), 0),
        };
    };

    const filteredTotals = calculateFilteredTotals();

    const formatNumber = (value) => {
        if (value === null || value === undefined || isNaN(value)) return '0';
        return parseFloat(value).toFixed(0);
    };

    const sortByColumn = (column) => {
        const sorted = [...tableData].sort((a, b) => {
            const aVal = a[column]?.toString().toLowerCase() || '';
            const bVal = b[column]?.toString().toLowerCase() || '';
            return aVal.localeCompare(bVal);
        });
        setTableData(sorted);
    };

    const filterByCustomer = (customer) => {
        if (!customer || customer === 'all') {
            showData();
            setIsFiltered(false);
        } else {
            const filtered = inboundData.filter(entry => 
                entry.customer_company?.toLowerCase().includes(customer.toLowerCase())
            );
            const formatted = filtered.map((entry) => {
                const newEntry = {};
                displayColumns.forEach(({ key }) => {
                    if (key === 'date') {
                        const dateObj = new Date(entry[key]);
                        newEntry[key] = isNaN(dateObj)
                            ? entry[key]
                            : `${String(dateObj.getDate()).padStart(2, '0')}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${dateObj.getFullYear()}`;
                    } else {
                        newEntry[key] = entry[key] ?? '—';
                    }
                });
                return newEntry;
            });
            setTableData(formatted);
            setIsFiltered(true);
        }
    };

    const handleDownload = () => {
        if (!allowDownload) {
            showAlert({
                message: 'Download not allowed. Please verify your password first.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
            return;
        }

        if (!tableData || tableData.length === 0) {
            showAlert({
                message: 'No data available for download.',
                type: 'warning',
                buttons: [{ type: 'warning', label: 'Close' }],
            });
            return;
        }

        const csvData = tableData.map(row => {
            const csvRow = {};
            displayColumns.forEach(({ key, label }) => {
                csvRow[label] = row[key] || '';
            });
            return csvRow;
        });

        downloadReportCSV(csvData, 'Inbound_Report');
    };

    // Get unique customers for filtering
    const uniqueCustomers = [...new Set(inboundData.map(entry => entry.customer_company))].sort();

    return (
        <div className="container-fluid p-0">
            <RequestPassword
                theme={theme}
                isOpen={openRequestPassword}
                setIsOpen={setOpenRequestPassword}
                returnData={handlePasswordSubmission}
            />

            {tableData && tableData.length > 0 && allowDownload ? (
                <div>
                    {/* Summary Stats */}
                    <div className="row mb-3">
                        <div className="col-12">
                            <div className={`card ${isLightTheme ? 'border-dark' : 'border-light'}`}>
                                <div className={`card-header ${isLightTheme ? 'bg-light text-dark' : 'bg-dark text-light'}`}>
                                    <h5 className="mb-0">📦 Inbound Reports Summary</h5>
                                </div>
                                <div className={`card-body ${isLightTheme ? 'bg-light text-dark' : 'bg-dark text-light'}`}>
                                    <div className="row">
                                        <div className="col-md-3">
                                            <strong>Total Orders:</strong> {summaryStats.totalOrders}
                                        </div>
                                        <div className="col-md-3">
                                            <strong>Unique Customers:</strong> {summaryStats.uniqueCustomers}
                                        </div>
                                        <div className="col-md-3">
                                            <strong>Total Pallets:</strong> {formatNumber(summaryStats.totalPallets)}
                                        </div>
                                        <div className="col-md-3">
                                            <strong>Total Quantity:</strong> {formatNumber(summaryStats.totalQuantity)}
                                        </div>
                                    </div>
                                    <div className="row mt-2">
                                        <div className="col-md-6">
                                            <strong>Total Log Entries:</strong> {formatNumber(summaryStats.totalEntries)}
                                        </div>
                                        <div className="col-md-6">
                                            <strong>Report Generated:</strong> {new Date().toLocaleDateString('en-GB')}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Controls */}
                    <div className="row mb-3">
                        <div className="col-12">
                            <div className={`card ${isLightTheme ? 'border-dark' : 'border-light'}`}>
                                <div className={`card-body ${isLightTheme ? 'bg-light text-dark' : 'bg-dark text-light'}`}>
                                    <div className="row align-items-center">
                                        <div className="col-md-12">
                                            <label className="form-label"><strong>Filter by Customer:</strong></label>
                                            <select
                                                className={`form-select ${isLightTheme ? '' : 'bg-dark text-light border-secondary'}`}
                                                onChange={(e) => filterByCustomer(e.target.value)}
                                                style={{ width: '100%' }}
                                            >
                                                <option value="all">All Customers</option>
                                                {uniqueCustomers.map(customer => (
                                                    <option key={customer} value={customer}>{customer}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Data Table */}
                    <div className="row">
                        <div className="col-12">
                            <div className={`card ${isLightTheme ? 'border-dark' : 'border-light'}`}>
                                <div className={`card-header ${isLightTheme ? 'bg-light text-dark' : 'bg-dark text-light'}`}>
                                    <h5 className="mb-0">Inbound Orders Data ({tableData.length} orders)</h5>
                                </div>
                                <div className={`card-body ${isLightTheme ? 'bg-light text-dark' : 'bg-dark text-light'} p-0`}>
                                    <div className="table-responsive">
                                        <table className={`table table-striped table-hover mb-0 ${isLightTheme ? 'table-light' : 'table-dark'}`}>
                                            <thead className={isLightTheme ? 'table-dark' : 'table-light'}>
                                                <tr>
                                                    {displayColumns.map((column) => (
                                                        <th 
                                                            key={column.key} 
                                                            className="text-center"
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => sortByColumn(column.key)}
                                                            title={`Click to sort by ${column.label}`}
                                                        >
                                                            {column.label}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {tableData.map((row, index) => (
                                                    <tr key={index}>
                                                        <td className="text-center">{row.date || 'N/A'}</td>
                                                        <td className="text-center">{row.customer_company || 'N/A'}</td>
                                                        <td className="text-center">
                                                            {row.product_names?.length > 50 ? (
                                                                <span title={row.product_names}>
                                                                    {row.product_names.substring(0, 50)}...
                                                                </span>
                                                            ) : (
                                                                row.product_names || 'N/A'
                                                            )}
                                                        </td>
                                                        <td className="text-center">{formatNumber(row.total_pallet_count)}</td>
                                                        <td className="text-center">{formatNumber(row.total_quantity)}</td>
                                                        <td className="text-center">{formatNumber(row.entry_count)}</td>
                                                        <td className="text-center">{row.id_range || 'N/A'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot className={isLightTheme ? 'table-secondary' : 'table-light'}>
                                                <tr>
                                                    <td className="text-center"><strong>TOTALS</strong></td>
                                                    <td className="text-center"><strong>—</strong></td>
                                                    <td className="text-center"><strong>—</strong></td>
                                                    <td className="text-center"><strong>{formatNumber(filteredTotals.totalPallets)}</strong></td>
                                                    <td className="text-center"><strong>{formatNumber(filteredTotals.totalQuantity)}</strong></td>
                                                    <td className="text-center"><strong>{formatNumber(filteredTotals.totalEntries)}</strong></td>
                                                    <td className="text-center"><strong>—</strong></td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : !allowDownload ? (
                <div className="row">
                    <div className="col-12">
                        <div className={`alert ${isLightTheme ? 'alert-warning' : 'alert-dark'} text-center`}>
                            <h4>🔐 Access Required</h4>
                            <p>Please enter your password to view the inbound reports.</p>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="row">
                    <div className="col-12">
                        <div className={`alert ${isLightTheme ? 'alert-warning' : 'alert-dark'} text-center`}>
                            <h4>📭 No Inbound Data Available</h4>
                            <p>No inbound data found for the selected date range. Try selecting a different date range.</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InboundReport;